import React from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Loader2, FileSignature, CheckCircle2, XCircle, Download, ExternalLink, Send, FileText, History,
} from 'lucide-react';
import { fmtEur, fmtDate, EMAIL_LOG_TYPE_STYLE } from './propositionsSigneesHelpers';
import { toast } from 'sonner';

export default function PropositionsSigneesModals({
  pdfPreview, closePdfPreview, resendOpen, setResendOpen, resendForm, setResendForm,
  handleResendPdfByEmail, resendSending,
  refuserOpen, setRefuserOpen, refuserTarget, refuserRaison, setRefuserRaison, refuserSubmitting, submitRefuser,
  validerOpen, setValiderOpen, validerTarget, validerNote, setValiderNote, validerSubmitting, submitValider,
  emailLog, setEmailLog,
}) {
  return (
    <>
      {/* MODAL VISUALISATION PDF SIGNE — V36.42 */}
      <Dialog open={!!pdfPreview} onOpenChange={(open) => !open && closePdfPreview()}>
        <DialogContent className="max-w-5xl w-[95vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="pdf-preview-dialog">
          <DialogHeader className="px-4 py-3 border-b bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white flex flex-row items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md shrink-0">
                <FileSignature className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base font-bold text-white truncate">PDF signé électroniquement</DialogTitle>
                <DialogDescription className="text-xs text-slate-300 truncate">
                  {pdfPreview?.fileName || ''} {pdfPreview?.item?.signer_full_name ? `· Signé par ${pdfPreview.item.signer_full_name}` : ''}
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {pdfPreview && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border-amber-400/40"
                    onClick={() => setResendOpen((v) => !v)}
                    data-testid="pdf-preview-resend-toggle"
                    title="Renvoyer le PDF par email au signataire"
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    Renvoyer par email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = pdfPreview.blobUrl;
                      a.download = pdfPreview.fileName;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      toast.success('Téléchargement lancé');
                    }}
                    data-testid="pdf-preview-download"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Télécharger
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                    onClick={() => window.open(pdfPreview.blobUrl, '_blank', 'noopener,noreferrer')}
                    data-testid="pdf-preview-open-tab"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    Nouvel onglet
                  </Button>
                </>
              )}
            </div>
          </DialogHeader>

          {/* V36.43 — Panneau de renvoi PDF par email */}
          {resendOpen && pdfPreview && (
            <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-b border-amber-300 px-4 py-3" data-testid="pdf-resend-panel">
              <div className="flex items-center gap-2 mb-2">
                <Send className="h-4 w-4 text-amber-700" />
                <span className="text-sm font-bold text-amber-900">Renvoyer le PDF par email</span>
                <span className="text-xs text-amber-700/80">
                  · Destinataire par défaut : <strong className="font-mono">{pdfPreview.item?.signer_email || '—'}</strong>
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="email"
                  placeholder={pdfPreview.item?.signer_email ? `(par defaut) ${pdfPreview.item.signer_email}` : 'Adresse email destinataire'}
                  value={resendForm.to_email}
                  onChange={(e) => setResendForm({ ...resendForm, to_email: e.target.value })}
                  className="text-sm px-3 py-1.5 rounded-md border border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  data-testid="pdf-resend-email-input"
                />
                <input
                  type="text"
                  placeholder="Message personnel (optionnel) : raison du renvoi..."
                  value={resendForm.message}
                  onChange={(e) => setResendForm({ ...resendForm, message: e.target.value })}
                  className="text-sm px-3 py-1.5 rounded-md border border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  data-testid="pdf-resend-message-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2 mt-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setResendOpen(false); setResendForm({ to_email: '', message: '' }); }}
                  className="text-amber-700 hover:bg-amber-100"
                >
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={handleResendPdfByEmail}
                  disabled={resendSending}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="pdf-resend-confirm-btn"
                >
                  {resendSending
                    ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    : <Send className="h-3.5 w-3.5 mr-1" />}
                  Envoyer
                </Button>
              </div>
            </div>
          )}
          <div className="flex-1 bg-slate-100 overflow-hidden">
            {pdfPreview?.blobUrl ? (
              <iframe
                src={pdfPreview.blobUrl}
                title="PDF signé"
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL REFUSER */}
      <Dialog open={refuserOpen} onOpenChange={setRefuserOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="text-red-700">Refuser la proposition</DialogTitle>
            <DialogDescription>
              {refuserTarget?.devis_id} — Le signataire <strong>{refuserTarget?.signer_full_name}</strong> recevra un email avec votre raison.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              placeholder="Raison du refus (visible par le signataire)…"
              value={refuserRaison}
              onChange={(e) => setRefuserRaison(e.target.value)}
              rows={4}
              maxLength={500}
              disabled={refuserSubmitting}
              data-testid="refuser-raison-input"
            />
            <p className="text-[11px] text-slate-500 mt-1 text-right">
              {refuserRaison.length}/500
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefuserOpen(false)} disabled={refuserSubmitting}>
              Annuler
            </Button>
            <Button
              onClick={submitRefuser}
              disabled={refuserSubmitting || refuserRaison.trim().length < 3}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="refuser-confirm-btn"
            >
              {refuserSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              Refuser et notifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL VALIDER */}
      <Dialog open={validerOpen} onOpenChange={setValiderOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-emerald-700">Valider la proposition</DialogTitle>
            <DialogDescription>
              {validerTarget?.devis_id} — Signée par <strong>{validerTarget?.signer_full_name}</strong> pour un montant TTC de <strong>{fmtEur(validerTarget?.montant_ttc)}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <Textarea
              placeholder="Note interne (optionnelle, non envoyée au signataire)…"
              value={validerNote}
              onChange={(e) => setValiderNote(e.target.value)}
              rows={2}
              maxLength={500}
              disabled={validerSubmitting}
              data-testid="valider-note-input"
            />
            <div className="bg-emerald-50 border-l-4 border-emerald-400 rounded p-3 text-xs text-emerald-900">
              Après validation, vous pouvez créer immédiatement la souscription
              dans le workflow officiel (snapshot pré-rempli) ou y revenir plus tard
              via le filtre &laquo;&nbsp;Validées&nbsp;&raquo;.
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setValiderOpen(false)} disabled={validerSubmitting}>
              Annuler
            </Button>
            <Button
              onClick={() => submitValider('stay')}
              disabled={validerSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="valider-confirm-btn"
            >
              {validerSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              Valider
            </Button>
            <Button
              onClick={() => submitValider('goto')}
              disabled={validerSubmitting}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
              data-testid="valider-confirm-goto-btn"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              Valider + Créer la souscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V37.80 — MODAL JOURNAL « EMAILS ENVOYÉS » */}
      <Dialog open={!!emailLog} onOpenChange={(open) => !open && setEmailLog(null)}>
        <DialogContent className="max-w-2xl" data-testid="proposition-email-log-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-700" />
              Journal « Emails envoyés » — <span className="font-mono text-base">{emailLog?.devis_id}</span>
            </DialogTitle>
            <DialogDescription>
              Traçabilité complète des envois (exclusivement manuels) : qui a envoyé quoi, à qui et quand.
              {emailLog?.membre_email ? <> Membre : <strong>{emailLog.membre_email}</strong>.</> : null}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
            {(emailLog?.entries || []).length === 0 ? (
              <p className="text-sm text-slate-500 italic py-6 text-center" data-testid="email-log-empty">
                Aucun email envoyé pour cette proposition.
              </p>
            ) : (
              (emailLog?.entries || []).map((e, i) => (
                <div
                  key={i}
                  className="border border-slate-200 rounded-lg p-3 bg-white"
                  data-testid={`email-log-entry-${i}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-[10px] font-semibold ${EMAIL_LOG_TYPE_STYLE[e.type] || 'bg-slate-600 text-white border-slate-800'}`}>
                      {e.label}
                    </Badge>
                    {e.success ? (
                      <Badge className="text-[10px] bg-emerald-700 text-white border-emerald-900 font-semibold">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Envoyé
                      </Badge>
                    ) : (
                      <Badge className="text-[10px] bg-red-700 text-white border-red-900 font-semibold">
                        <XCircle className="h-3 w-3 mr-1" /> Échec
                      </Badge>
                    )}
                    {e.details?.pdf_attached && (
                      <span className="text-[10px] text-slate-500"><FileText className="h-3 w-3 inline" /> PDF joint</span>
                    )}
                    {e.details?.custom_message && (
                      <span className="text-[10px] text-slate-500">Message personnalisé</span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 mt-2 text-xs text-slate-700">
                    <div>
                      <span className="text-slate-400">À :</span>{' '}
                      <strong>{e.to_email || '—'}</strong>
                      {e.to_name ? <span className="text-slate-500"> ({e.to_name})</span> : null}
                    </div>
                    <div>
                      <span className="text-slate-400">Par :</span>{' '}
                      <strong>{e.sent_by || '—'}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400">Le :</span>{' '}
                      <strong>{e.sent_at ? fmtDate(e.sent_at) : '—'}</strong>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailLog(null)} data-testid="email-log-close">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
